import { useEffect, useRef, useState } from "react";
import { TRACKING_ID_KEY } from "~/components/analytics/useTrackingId";
import { AnalyticsEvents, trackEvent } from "~/connectors/analytics.connector";
import { customerOrdersService } from "~/connectors/orders.connector";
import { ChildInfo, Order, OrderFormDataDetails } from "~/types";


type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export default function useOrderInitialization({
  authStatus,
  anonymousId,
  orderIdSearchParam,
  router,
}: {
  authStatus: AuthStatus;
  anonymousId: string | null;
  orderIdSearchParam: number | null;
  router: any;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<OrderFormDataDetails>({});
  const [order, setOrder] = useState<Order | null>(null);
  const hasLoadedRef = useRef(false);
  const [previousChildren, setPreviousChildren] = useState<ChildInfo[]>([]);

  async function fetchAnonymousDraftOrder(anonymousIdLocal: string) {
    const draft =
      await customerOrdersService.getAnonymousDraft(anonymousIdLocal);
    if (draft?.data) {
      return draft.data as OrderFormDataDetails;
    }
    return null;
  }

  useEffect(() => {
    if (authStatus === "loading" || hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const transferAnonymousDraft = async () => {
      if (!anonymousId) {
        throw new Error("No anonymousId available for transfer.");
      }
      const draftOrder = await customerOrdersService.upsertDraft({
        anonymousId,
      });
      // Clear the anonymous ID from localStorage after successful transfer
      if (typeof window !== "undefined") {
        localStorage.removeItem(TRACKING_ID_KEY);
      }

      // Update state with returned Order data
      if (draftOrder) {
        setFormData(draftOrder.formData || {});
        setOrder(draftOrder);
      }
    };

    const loadData = async () => {
      setIsLoading(true);

      // Restore order from URL (but don't create payment intent here)
      if (orderIdSearchParam) {
        try {
          const fetchedOrder =
            await customerOrdersService.getOrder(orderIdSearchParam);
          if (fetchedOrder && fetchedOrder.paymentStatus !== "paid") {
            setOrder(fetchedOrder);
            setFormData(fetchedOrder.formData || {});

            trackEvent(AnalyticsEvents.ORDER_PAYMENT_REINITIATED, {
              orderId: fetchedOrder.id,
              amount: fetchedOrder.total,
            });

            router.push(`/comanda/plata?orderId=${fetchedOrder.id}`);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error restoring order from query parameter:", error);
        }
      }

      const anonymousIdInUrl =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("orderAid")
          : null;

      if (authStatus === "authenticated") {
        try {
          const childrenData = await customerOrdersService.getChildrenInfo();
          const items = childrenData.data;
          if (items) setPreviousChildren(items);
        } catch (error) {
          console.error("Error loading previous children info:", error);
        }

        if (anonymousIdInUrl) {
          await transferAnonymousDraft();
        } else {
          const draftOrder = await customerOrdersService.getDraft();
          if (draftOrder) {
            setOrder(draftOrder);
            setFormData(draftOrder.formData || {});
          }
        }
      } else if (authStatus === "unauthenticated" && anonymousId) {
        const anonymousDraftOrder = await fetchAnonymousDraftOrder(anonymousId);
        if (anonymousDraftOrder) setFormData(anonymousDraftOrder);
      } else {
        throw new Error(
          "Cannot load form data: unknown authentication status or missing anonymous ID.",
        );
      }

      setIsLoading(false);
    };

    loadData();
  }, [authStatus, anonymousId, orderIdSearchParam, router]);

  return {
    isLoading,
    order,
    setOrder,
    formData,
    setFormData,
    previousChildren,
  };
}
